import {
  Activity,
  BadgeCheck,
  BookOpenCheck,
  BellRing,
  Boxes,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  FileClock,
  FilePenLine,
  FileSearch,
  FolderTree,
  Gauge,
  History,
  KeyRound,
  Menu,
  PackageSearch,
  PackagePlus,
  Sparkles,
  RefreshCw,
  Settings,
  ShieldCheck,
  Store,
  ShoppingBag,
  Tag,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAppData } from "./context";
import type { BatchTaskListItem, DetailStatus, TaskStatus, TaskType } from "./types";

export const typeLabels: Record<TaskType, string> = {
  CONTENT_UPDATE: "标题与描述维护",
  NET_PROCEEDS_UPDATE: "批量收益调价",
  TITLE_UPDATE: "批量改标题",
  INVENTORY_UPDATE: "批量改库存",
  DESCRIPTION_UPDATE: "批量改描述",
  LISTING_TYPE_UPDATE: "批量升级 Premium",
};

export const typeShort: Record<TaskType, string> = {
  CONTENT_UPDATE: "CT",
  NET_PROCEEDS_UPDATE: "NP",
  TITLE_UPDATE: "TT",
  INVENTORY_UPDATE: "ST",
  DESCRIPTION_UPDATE: "DS",
  LISTING_TYPE_UPDATE: "LT",
};

const navItems = [
  { to: "/", label: "工作站", icon: Gauge, end: true },
  { to: "/repricing", label: "调价工作台", icon: CircleDollarSign },
  { to: "/new-task", label: "批量上传", icon: Upload },
  { to: "/ai-content", label: "AI 内容", icon: Sparkles },
  { to: "/listing-upload", label: "商品上架", icon: PackagePlus },
  { to: "/tasks", label: "任务中心", icon: FileClock },
  { to: "/approvals", label: "审批中心", icon: ClipboardCheck },
  { to: "/product-mappings", label: "商品映射", icon: PackageSearch },
  { to: "/commerce-insights", label: "经营分析", icon: CircleDollarSign },
  { to: "/categories", label: "类目中心", icon: FolderTree },
  { to: "/orders", label: "订单同步", icon: ShoppingBag },
  { to: "/order-exceptions", label: "异常订单", icon: BellRing },
  { to: "/accounts", label: "账号管理", icon: Store },
  { to: "/api-access", label: "API 接入", icon: KeyRound },
  { to: "/audit", label: "审计中心", icon: History },
  { to: "/settings", label: "系统设置", icon: Settings },
];

export function Shell() {
  const { data, loading, error, refresh } = useAppData();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
    window.requestAnimationFrame(() => document.getElementById("main-content")?.focus({ preventScroll: true }));
  }, [location.pathname]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳到主内容</a>
      <button className={`mobile-scrim ${open ? "visible" : ""}`} aria-label="关闭导航" onClick={() => setOpen(false)} />
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <div className="brand-block">
          <div className="brand-mark" aria-hidden="true"><span>MF</span></div>
          <div>
            <strong>MeliForge</strong>
            <small>批量维护工作站</small>
          </div>
          <button className="icon-button sidebar-close" aria-label="关闭导航" title="关闭导航" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <div className="environment-strip">
          <span className="status-dot amber" />
          <div>
            <strong>{!data ? "状态同步中" : data.mode === "mock" ? "自动化测试" : data.writeEnabled ? "生产写入已启用" : "生产只读验证"}</strong>
            <small>{!data ? "正在读取服务器状态" : data.writeEnabled ? "授权店铺批量任务可执行" : "商品写入已锁定"}</small>
          </div>
        </div>
        <nav className="primary-nav" aria-label="主导航">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => (isActive ? "active" : "")}>
                <Icon size={18} strokeWidth={1.8} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="capability-lock"><ShieldCheck size={17} /><span>{!data ? "真实 API 状态同步中" : data.writeEnabled ? "真实 API · 商品归属校验" : "真实 API 已接入 · 写入锁定"}</span></div>
          <small>规则版本 {data?.settings.ruleVersion ?? "--"}</small>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-menu" aria-label="打开导航" title="打开导航" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <div className="topbar-context">
            <span className="eyebrow">MERCADO LIBRE OPERATIONS</span>
            <span className="context-separator" />
            <span>{!data ? "状态同步中" : data.mode === "mock" ? "Mock 测试" : data.writeEnabled ? "生产写入" : "生产只读"}</span>
          </div>
          <div className="topbar-actions">
            <span className={`sync-state ${loading ? "loading" : ""}`}><Activity size={15} />{loading ? "同步中" : "已同步"}</span>
            <button className="icon-button" aria-label="刷新数据" title="刷新数据" onClick={() => void refresh()}><RefreshCw size={17} /></button>
          </div>
        </header>
        {error ? <div className="global-error" role="alert">{error}</div> : null}
        <main id="main-content" className="main-content" tabIndex={-1}><Outlet /></main>
      </div>
    </div>
  );
}

export function PageHeader({ title, eyebrow, actions, backTo }: { title: string; eyebrow: string; actions?: ReactNode; backTo?: string }) {
  return (
    <div className="page-header">
      <div>
        {backTo ? <Link to={backTo} className="back-link">返回<ChevronRight size={14} /></Link> : null}
        <span className="page-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}

export function StatusBadge({ status }: { status: TaskStatus | DetailStatus | string }) {
  const labels: Record<string, string> = {
    DRAFT: "草稿",
    VALIDATING: "预检中",
    READY: "待执行",
    WAITING_APPROVAL: "待审批",
    RUNNING: "执行中",
    PAUSED: "已暂停",
    VERIFYING: "回读中",
    COMPLETED: "已完成",
    CANCELLED: "已取消",
    REJECTED: "已驳回",
    PENDING: "待处理",
    BLOCKED: "已拦截",
    SUBMITTING: "提交中",
    RETRYING: "重试中",
    SUCCESS: "成功",
    FAILED: "失败",
    NEEDS_REVIEW: "待复核",
    SKIPPED_NO_CHANGE: "无变化",
    PROCESSING: "生成中",
    REVIEWING: "待审核",
    PARTIAL: "部分失败",
    GENERATING: "生成中",
    PENDING_REVIEW: "待自动处理",
    APPROVED: "已就绪",
    GENERATION_FAILED: "生成失败",
    SUBMITTED: "已提交",
  };
  const tone = ["SUCCESS", "COMPLETED", "APPROVED", "SUBMITTED"].includes(status) ? "positive" : ["FAILED", "REJECTED", "CANCELLED", "GENERATION_FAILED"].includes(status) ? "negative" : ["WAITING_APPROVAL", "NEEDS_REVIEW", "BLOCKED", "PAUSED", "PARTIAL", "PENDING_REVIEW"].includes(status) ? "warning" : ["RUNNING", "VERIFYING", "SUBMITTING", "RETRYING", "PROCESSING", "GENERATING"].includes(status) ? "active" : "neutral";
  return <span className={`status-badge ${tone}`}><span />{labels[status] ?? status}</span>;
}

export function MetricStrip({ items }: { items: Array<{ label: string; value: string | number; note?: string; tone?: string }> }) {
  return (
    <div className="metric-strip">
      {items.map((item) => (
        <div className={`metric-cell ${item.tone ?? ""}`} key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          {item.note ? <small>{item.note}</small> : null}
        </div>
      ))}
    </div>
  );
}

export function ExecutionRail({ tasks, activeTask }: { tasks: BatchTaskListItem[]; activeTask?: BatchTaskListItem }) {
  const stages = useMemo<Array<{ label: string; count: number; done?: boolean; active?: boolean }>>(() => {
    if (activeTask) {
      const order = [
        { label: "导入", done: true, count: activeTask.summary.total },
        { label: "预检", done: true, count: activeTask.summary.executable },
        { label: "审批", done: !activeTask.riskReasons.length || activeTask.status !== "WAITING_APPROVAL", active: activeTask.status === "WAITING_APPROVAL", count: activeTask.riskReasons.length },
        { label: "执行", done: ["VERIFYING", "COMPLETED"].includes(activeTask.status), active: ["RUNNING", "PAUSED"].includes(activeTask.status), count: activeTask.summary.success + activeTask.summary.failed + activeTask.summary.review },
        { label: "回读", done: activeTask.status === "COMPLETED", active: activeTask.status === "VERIFYING", count: activeTask.summary.processing },
        { label: "完成", done: activeTask.status === "COMPLETED", count: activeTask.summary.success },
      ];
      return order;
    }
    return [
      { label: "导入", count: tasks.length },
      { label: "预检", count: tasks.filter((task) => ["READY", "WAITING_APPROVAL"].includes(task.status)).length },
      { label: "审批", count: tasks.filter((task) => task.status === "WAITING_APPROVAL").length },
      { label: "执行", count: tasks.filter((task) => ["RUNNING", "PAUSED"].includes(task.status)).length },
      { label: "回读", count: tasks.filter((task) => task.status === "VERIFYING").length },
      { label: "完成", count: tasks.filter((task) => task.status === "COMPLETED").length },
    ];
  }, [activeTask, tasks]);

  return (
    <div className="execution-rail" aria-label="任务执行轨道">
      {stages.map((stage, index) => (
        <div className={`rail-stage ${stage.done ? "done" : ""} ${stage.active ? "active" : ""}`} key={stage.label}>
          <div className="rail-node">{stage.done ? <BadgeCheck size={16} /> : <span>{index + 1}</span>}</div>
          <div><strong>{stage.label}</strong><small>{stage.count}</small></div>
        </div>
      ))}
    </div>
  );
}

export function SectionHeading({ title, meta, action }: { title: string; meta?: string; action?: ReactNode }) {
  return <div className="section-heading"><div><h2>{title}</h2>{meta ? <span>{meta}</span> : null}</div>{action}</div>;
}

export function LoadingState() {
  return <div className="loading-grid" aria-label="正在加载"><span /><span /><span /></div>;
}

export function EmptyState({ icon = "tasks", title, action }: { icon?: "tasks" | "audit"; title: string; action?: ReactNode }) {
  const Icon = icon === "audit" ? FileSearch : Boxes;
  return <div className="empty-state"><Icon size={28} /><strong>{title}</strong>{action}</div>;
}

export function Pagination({ page, total, pageSize, onChange }: { page: number; total: number; pageSize: number; onChange: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return <div className="pagination" aria-label="分页">
    <span>第 {page} / {pages} 页 · {total.toLocaleString()} 条</span>
    <div>
      <button className="icon-button" aria-label="上一页" title="上一页" disabled={page <= 1} onClick={() => onChange(page - 1)}><ChevronLeft size={16} /></button>
      <button className="icon-button" aria-label="下一页" title="下一页" disabled={page >= pages} onClick={() => onChange(page + 1)}><ChevronRight size={16} /></button>
    </div>
  </div>;
}

export function TaskTypeIcon({ type, size = 18 }: { type: TaskType; size?: number }) {
  const Icon = type === "NET_PROCEEDS_UPDATE"
    ? CircleDollarSign
    : type === "TITLE_UPDATE"
      ? FilePenLine
      : type === "CONTENT_UPDATE"
        ? FilePenLine
      : type === "INVENTORY_UPDATE"
        ? Boxes
        : type === "DESCRIPTION_UPDATE"
          ? BookOpenCheck
          : Tag;
  return <Icon size={size} />;
}

export function formatDate(value?: string, withTime = true) {
  if (!value) return "--";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}) }).format(new Date(value));
}

export function readValue(value: Record<string, unknown>) {
  const entry = value.targetNetProceeds ?? value.price ?? value.netProceeds ?? value.availableQuantity ?? value.title ?? value.description;
  if (entry === undefined) return JSON.stringify(value);
  const text = String(entry);
  return text.length > 54 ? `${text.slice(0, 54)}…` : text;
}
